<template>
  <div class="flex flex-col w-full">
    <div v-if="label" class="text-sm text-secondary-invert py-2">
      {{ label }}
    </div>
    <div class="flex">
      <div
        class="w-20 h-20 text-secondary-invert border border-dashed border-secondary-lighter rounded flex flex-shrink-0 justify-center items-center overflow-none"
      >
        <image-icon v-if="!image.optimized" class="w-6 h-6 fill-current" />
        <div v-else class="w-full h-full gp-image-picker-preview">
          <img :src="image.optimized" class="h-full m-auto" />
        </div>
      </div>
      <div
        v-if="value && value !== 'none'"
        class="pl-4 flex-grow text-secondary-invert text-sm"
      >
        <div class="break-all">{{ filename }}</div>
        <div class="text-xs" v-if="image.dimensions.width">
          {{ image.dimensions.width }}px x {{ image.dimensions.height }}px
        </div>
        <div class="text-xs" v-if="image.dimensions.size">
          {{ image.dimensions.size }}kb
        </div>
      </div>
      <div v-if="value && value !== 'none'" class="flex items-center text-secondary-invert">
        <trash-icon
          class="w-4 h-4 fill-current cursor-pointer"
          @click="onRemove"
        />
      </div>
    </div>
    <div class="flex my-4">
      <gd-button
        id="gp-styleEditor-chooseImage"
        data-cy="styleEditorChooseImage"
        variant="primary"
        size="expand"
        @click="openImagePickerModal"
        >{{ $t('frontend_sites_image_picker_choose') }}</gd-button
      >
    </div>
    <gd-modal ref="imagePicker" name="image-picker" width="100%" height="100%">
      <image-picker
        :value="value"
        modal="true"
        :remove="remove"
        @close-modal="$refs.imagePicker.close()"
        @selected="onSelect"
      />
    </gd-modal>
  </div>
</template>

<script>
import ImageIcon from '../assets/image-icon.svg';
import TrashIcon from '../assets/trash.svg';
import { gdImgProxy } from '../utils';
import GdButton from './Button';
import GdDropdown from './Dropdown';
import ImagePicker from './ImagePicker';
import ImagePickerEditor from './ImagePickerEditor';
import GdModal from './Modal';
import TextInput from './TextInput';

export default {
  name: 'ImagePickerModal',
  components: {
    ImagePickerEditor,
    GdDropdown,
    GdModal,
    GdButton,
    ImagePicker,
    ImageIcon,
    TextInput,
    TrashIcon
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {},
    component: {},
    removePadding: {},
    primary: { default: false },
    getFullUrl: { default: false }
  },
  data() {
    return {
      image: {
        url: '',
        optimized: '',
        dimensions: {
          width: undefined,
          height: undefined,
          size: undefined
        }
      },
      remove: false
    };
  },
  computed: {
    previewImage() {
      if (!this.value || this.value === 'none') return '';
      if (this.value.includes('linear-gradient(')) {
        return encodeURI(this.value);
      }

      if (!this.isBase64) {
        return `url('${this.image.optimized}')`;
      }

      return `url('${encodeURI(this.value)}')`;
    },
    isGradient() {
      return this.value.includes('linear-gradient(');
    },
    isBase64() {
      return this.value && this.value.includes('base64');
      // return this.value?.includes('base64');
    },
    filename() {
      if (!this.value || this.isBase64) return '';

      if (this.isGradient) {
        return 'linear-gradient';
      }

      let filename = this.image.url.split('/').pop();
      const extension = '.' + filename.split('.').pop();
      filename = filename.replace(extension, '');

      const count = 20;
      const result =
        filename.slice(0, count) +
        (filename.length > count ? '...' : '') +
        extension;

      return result;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.resetImage();
        if (value) {
          if (value.includes('base64')) {
            this.image.url = value;
            this.image.optimized = value;
          } else {
            if (value.startsWith(window.gdEnv.VUE_APP_IMAGES_URL)) {
              this.image.url = value
                .substr(value.indexOf('/plain') + 7)
                .replace('@webp', '');
              this.image.optimized = value;
            } else {
              this.image.url = value;
              this.generateOptimizedUrl();
            }
            this.getDimensionsAndSize();
          }
        } else {
          if (!this.remove) {
            if (this.component) {
              const img = this.component.element.querySelector('img');
              if (img) {
                const href = img.getAttribute('src');
                this.onSelect(href);
              }
            }
            this.remove = false;
          }
          return;
        }
      }
    }
  },
  mounted() {
    window.vm = this;
  },
  methods: {
    openImagePickerModal() {
      this.$refs.imagePicker.open();
    },
    openImageEditorModal() {
      this.$refs.imageEditor.open();
    },
    async getDimensionsAndSize() {
      this.image.dimensions = {
        size: undefined,
        height: undefined,
        width: undefined
      };
      // Get image dimensions
      const img = new Image();
      img.onload = () => {
        this.image.dimensions.height = img.height;
        this.image.dimensions.width = img.width;
      };
      img.src = this.image.optimized;
    },
    resetImage() {
      this.image = {
        url: '',
        optimized: '',
        dimensions: {
          width: undefined,
          height: undefined,
          size: undefined
        }
      };
    },
    onRemove() {
      this.remove = true;
      this.resetImage();
      this.onChange();
    },
    generateOptimizedUrl() {
      if (this.image.url) {
        this.image.optimized = gdImgProxy({
          url: this.image.url
        });
      }
    },
    onSelect(url) {
      if (url && !url.includes('base64')) {
        this.image.url = url;
        this.generateOptimizedUrl();
        this.getDimensionsAndSize();
        this.onChange();
      }
    },
    onEditComplete(image) {
      this.image = image;
      this.onChange();
    },
    onChange() {
      const imageUrl = this.getFullUrl ? this.image.url : this.image.optimized;
      this.$emit('input', imageUrl);
      this.$emit('change', imageUrl);
    }
  }
};
</script>
